/**
 * Media / Link / Title toggle row, hidden file input, and carousel preview for post creates.
 */
import React from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselDots,
} from "ui/Carousel";
import VideoPlayer from "components/Common/VideoPlayer";
import { ImagePlus, Link as LinkIcon, Type, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useMediaManager } from "hooks/useMediaManager";

export type MediaManagerApi = ReturnType<typeof useMediaManager>;

export interface MediaSectionProps {
  /** Prefix for element ids, e.g. "impact", "solution", "action" */
  idPrefix: string;
  mm: MediaManagerApi;
  showLink: boolean;
  showTitle: boolean;
  onToggleLink: () => void;
  onToggleTitle: () => void;
  /** When enabling link, some forms also force title on */
  onEnableLink?: () => void;
  /** Omitted when `includeLinkToggle` is false (e.g. action create). */
  linkField?: React.ReactNode;
  titleField: React.ReactNode;
  /** If false, only Media + Title toggles (action form) */
  includeLinkToggle?: boolean;
  /** When false, keep the title field visible and omit the title toggle button. */
  allowTitleToggle?: boolean;
}

const MediaSection: React.FC<MediaSectionProps> = ({
  idPrefix,
  mm,
  showLink,
  showTitle,
  onToggleLink,
  onToggleTitle,
  onEnableLink,
  linkField,
  titleField,
  includeLinkToggle = true,
  allowTitleToggle = true,
}) => {
  const uploadId = `${idPrefix}-media-upload`;

  const handleLinkClick = () => {
    onEnableLink?.();
    onToggleLink();
  };

  return (
    <div className="form-control">
      <div className="flex gap-2 mb-2">
        <button
          type="button"
          onClick={() => document.getElementById(uploadId)?.click()}
          className={`px-2 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${
            mm.media.length > 0 ? "btn-primary" : "btn-secondary"
          }`}
          aria-label="Add media"
        >
          <ImagePlus className="size-4" aria-hidden />
          <span>Media</span>
        </button>
        {includeLinkToggle ? (
          <button
            type="button"
            onClick={handleLinkClick}
            className={`px-2 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${
              showLink ? "btn-primary" : "btn-secondary"
            }`}
            aria-label="Toggle reference link"
          >
            <LinkIcon className="size-4" aria-hidden />
            <span>Link</span>
          </button>
        ) : null}
        {allowTitleToggle ? (
          <button
            type="button"
            onClick={onToggleTitle}
            className={`px-2 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${
              showTitle ? "btn-primary" : "btn-secondary"
            }`}
            aria-label="Toggle title field"
          >
            <Type className="size-4" aria-hidden />
            <span>Title</span>
          </button>
        ) : null}
      </div>

      <input
        id={uploadId}
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.gif,.webp,.apng,.avif,.svg,.mp4,.mpeg,.mov,.avi,.webm,.ogg,.3gp,.flv"
        className="hidden"
        onChange={(e) => mm.selectFiles(e.target.files)}
      />

      {mm.media.length > 0 ? (
        <div className="form-control mb-2">
          <div className="mt-4 relative">
            <Carousel
              className="w-full"
              setApi={(api) => {
                if (api) {
                  mm.carouselApiRef.current = api;
                  api.on("select", () => mm.setCurrentIdx(api.selectedScrollSnap()));
                }
              }}
            >
              <CarouselContent>
                {mm.media.map((m, idx) => (
                  <CarouselItem key={idx} className="md:basis-1/2 lg:basis-1/3">
                    <div
                      className="relative border rounded-lg overflow-hidden bg-transparent flex items-center justify-center w-full"
                      style={mm.aspectStyle()}
                    >
                      {m.type === "image" ? (
                        <img
                          src={m.preview}
                          alt={`Media ${idx + 1}`}
                          className="w-full h-full object-contain object-center"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <VideoPlayer
                          url={m.preview}
                          muted
                          controls
                          objectFit="contain"
                          className="w-full h-full"
                        />
                      )}
                      <div className="absolute top-2 right-2 flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => mm.move(idx, "left")}
                          disabled={idx === 0}
                          className="p-1 bg-white bg-opacity-80 rounded-full disabled:opacity-50"
                          aria-label="Move media left"
                        >
                          <ChevronLeft className="size-3" />
                        </button>
                        <div className="bg-white bg-opacity-80 text-black rounded-full px-2 py-1 text-xs">
                          {idx + 1}/{mm.media.length}
                        </div>
                        <button
                          type="button"
                          onClick={() => mm.move(idx, "right")}
                          disabled={idx === mm.media.length - 1}
                          className="p-1 bg-white bg-opacity-80 rounded-full disabled:opacity-50"
                          aria-label="Move media right"
                        >
                          <ChevronRight className="size-3" />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => mm.removeAt(idx)}
                        className="absolute top-2 left-2 size-7 rounded-full bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                        aria-label="Remove media"
                      >
                        <span className="flex items-center justify-center">
                          <X className="size-4 stroke-1" />
                        </span>
                      </button>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious transparent className="absolute left-2 top-1/2 -translate-y-1/2" />
              <CarouselNext transparent className="absolute right-2 top-1/2 -translate-y-1/2" />
              <CarouselDots />
            </Carousel>
          </div>
        </div>
      ) : null}

      {showLink && includeLinkToggle ? linkField : null}
      {showTitle ? titleField : null}
    </div>
  );
};

export default MediaSection;
